import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useContent } from "../../hooks/useContent";

const defaultSteps = [
  { num: "01", title: "Discovery", desc: "We immerse ourselves in your brand universe..." },
  { num: "02", title: "Strategy", desc: "Architecture before aesthetics..." },
  { num: "03", title: "Design", desc: "Where magic happens..." },
  { num: "04", title: "Development", desc: "Pixel-perfect implementation..." },
  { num: "05", title: "Launch", desc: "The beginning, not the end..." }
];

const DesktopProcessStep = ({ step, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "center center"]
    });
    
    const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1]);
    const x = useTransform(scrollYProgress, [0, 1], [index % 2 === 0 ? 50 : -50, 0]);
    const color = useTransform(scrollYProgress, [0, 0.9, 1], ["#333", "#666", "#CCFF00"]); 

    return (
        <motion.div 
            ref={ref}
            style={{ opacity, x }}
            className={`flex flex-col md:flex-row gap-8 md:gap-20 items-center mb-40 last:mb-0 ${index % 2 === 0 ? "md:text-right md:flex-row" : "md:flex-row-reverse md:text-left"}`}
        >
             <div className={`flex-1 ${index % 2 === 0 ? "md:pr-20" : "md:pl-20"}`}>
                <span className="text-accent font-mono text-xl md:text-2xl mb-4 block">/ {step.num}</span>
                <h4 className="text-4xl md:text-6xl font-display font-bold mb-6 text-white">{step.title}</h4>
                <p className="text-gray-400 text-lg leading-relaxed">{step.desc}</p>
            </div>

            <div className="hidden md:flex w-6 h-6 bg-black border-2 border-white/10 rounded-full z-10 relative items-center justify-center">
                 <motion.div 
                    style={{ backgroundColor: color, opacity: scrollYProgress }}
                    className="w-16 h-16 rounded-full absolute -z-10 blur-xl transition-colors duration-200"
                 />
                 <motion.div 
                    style={{ scale: scrollYProgress, backgroundColor: color }}
                    className="w-3 h-3 rounded-full transition-colors duration-200"
                 />
            </div>

            <div className="flex-1 hidden md:block"></div>
        </motion.div>
    )
}

const MobileProcessStep = ({ step }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 90%", "center center"]
    });
    
    const opacity = useTransform(scrollYProgress, [0, 1], [0.3, 1]);
    const x = useTransform(scrollYProgress, [0, 1], [30, 0]);
    const dotColor = useTransform(scrollYProgress, [0.8, 1], ["#333", "#CCFF00"]);
    const borderColor = useTransform(scrollYProgress, [0.8, 1], ["rgba(255,255,255,0.1)", "#CCFF00"]);

    return (
        <motion.div 
            ref={ref}
            style={{ opacity, x }}
            className="relative pr-4"
        >
             {/* Dynamic Dot */}
            <motion.div 
                style={{ borderColor: borderColor }}
                className="absolute -left-[31px] md:-left-[39px] top-1.5 w-4 h-4 bg-black border-2 rounded-full z-10 flex items-center justify-center transition-colors duration-200"
            >
                <motion.div 
                    style={{ backgroundColor: dotColor }}
                    className="w-1.5 h-1.5 rounded-full"
                />
            </motion.div>

            <span className="text-accent font-mono text-xs md:text-sm mb-2 block">/ {step.num}</span>
            <h4 className="text-2xl md:text-3xl font-display font-bold mb-3 text-white tracking-tight">{step.title}</h4>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-full">{step.desc}</p>
        </motion.div>
    );
}

const MobileProcess = ({ steps }) => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 70%", "end 50%"]
    });

    const scaleY = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    return (
        <div ref={containerRef} className="relative ml-2 pl-6 py-2 md:ml-4 md:pl-8">
             {/* Background Line */}
             <div className="absolute left-0 top-2 bottom-0 w-px bg-white/10" />
             
             {/* Animated Active Line */}
             <motion.div 
                style={{ scaleY, transformOrigin: "top" }}
                className="absolute left-0 top-2 bottom-0 w-px bg-accent shadow-[0_0_10px_#CCFF00]" 
             />

             <div className="flex flex-col gap-12 md:gap-16">
                 {steps.map((step, i) => (
                    <MobileProcessStep key={i} step={step} />
                ))}
            </div>
        </div>
    );
}

const DesktopProcess = ({ steps }) => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 20%", "end 50%"]
    });

    const scaleY = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    return (
        <div ref={containerRef} className="relative">
             {/* The Dynamic Line */}
            <div className="absolute top-40 left-1/2 -translate-x-1/2 w-px h-[calc(100%-320px)] bg-white/5 hidden md:block">
                <motion.div 
                    style={{ scaleY, transformOrigin: "top" }}
                    className="w-full bg-accent shadow-[0_0_15px_#CCFF00] h-full"
                />
            </div>

            <div className="flex flex-col">
                {steps.map((step, i) => (
                    <DesktopProcessStep key={i} step={step} index={i} />
                ))}
            </div>
        </div>
    );
}

const Process = () => {
    const [isMobile, setIsMobile] = useState(true);
    const { content } = useContent("process");
    const steps = content?.steps || defaultSteps;

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    return (
        <section className="py-20 md:py-40 px-4 md:px-12 bg-neutral-900 text-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center mb-16 md:mb-40">
                    <h2 className="text-xs md:text-sm font-bold uppercase tracking-widest text-accent mb-4 md:mb-6">The Process</h2>
                    <h3 className="text-4xl sm:text-5xl md:text-8xl font-display font-bold">
                        HOW WE <br/> <span className="text-stroke">WORK</span>
                    </h3>
                </div>

                {isMobile ? <MobileProcess steps={steps} /> : <DesktopProcess steps={steps} />}
            </div>
        </section>
    );
};

export default Process;
